import type { BadgeOptions, RadioGroupProps } from "@hightouch/formkit";
import type { SharedProps } from ".";
import { Controller } from "react-hook-form";
import { Badge, Radio, RadioGroup, Tooltip } from "@hightouch/htui";
import { Markdown } from "src/ui/markdown";
import { getIconComponent } from "src/formkit/utils";

export const FKRadioGroup = ({
  name,
  options,
  isDisabled,
  query,
}: RadioGroupProps & SharedProps) => {
  const staticOrDynamicOptions = query.enabled ? (query.data ?? []) : options;

  return (
    <Controller
      name={name}
      render={({ field }) => {
        // `RadioGroup` component requires values to be strings, but
        // field options can be either completely missing or be booleans
        // so we're using option indexes as `RadioGroup` value instead
        const selectedIndex = staticOrDynamicOptions.findIndex(
          (option) => option.value === (field.value ?? undefined),
        );

        return (
          <RadioGroup
            isDisabled={isDisabled}
            orientation="vertical"
            value={String(selectedIndex)}
            onChange={(indexString) => {
              const option = staticOrDynamicOptions.find(
                (_, index) => String(index) === indexString,
              );
              field.onChange(option?.value ?? null);
            }}
          >
            {staticOrDynamicOptions.map((option, index) => (
              <Radio
                key={option.value ?? index}
                description={
                  option.description && (
                    <Markdown>{option.description}</Markdown>
                  )
                }
                label={option.label}
                value={String(index)}
                badge={
                  option.badge ? (
                    <OptionBadge badge={option.badge} />
                  ) : undefined
                }
              />
            ))}
          </RadioGroup>
        );
      }}
    />
  );
};

const OptionBadge = ({ badge }: { badge: BadgeOptions }) => {
  const element = (
    <Badge
      variant={badge.variant || "subtle"}
      size={badge.size || "md"}
      svgIcon={badge.icon ? getIconComponent(badge.icon) : undefined}
    >
      {badge.text}
    </Badge>
  );

  return badge.tooltip ? (
    <Tooltip message={badge.tooltip}>{element}</Tooltip>
  ) : (
    element
  );
};
